import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Outlet, Route, Routes, Navigate } from 'react-router-dom';
import { App } from '@/app';
import { AppShell } from '@/layouts/app-shell';
import { LoginPage } from '@/pages/login';
import { DashboardPage } from '@/pages/dashboard';
import { ItemsListPage } from '@/pages/catalog/items-list';
import { ItemDetailPage } from '@/pages/catalog/item-detail';
import { ItemNewPage } from '@/pages/catalog/item-new';
import { CategoriesPage } from '@/pages/catalog/categories';
import { PriceListsPage } from '@/pages/pricing/price-lists';
import { CsvImportPage } from '@/pages/import-export/csv-import';
import { SanitexItemDiagnosisPage } from '@/pages/sanitex/item-diagnosis';
import { SanitexPendingCardAuthorisationsPage } from '@/pages/sanitex/pending-card-authorisations';
import { TerminalsListPage } from '@/pages/terminals/terminals-list';
import { KitchenOrdersPage } from '@/pages/terminals/kitchen-orders';
import { FiscalHoldsListPage } from '@/pages/terminals/fiscal-holds-list';
import { LinkDevicesPage } from '@/pages/terminals/link-devices';
import { TerminalNewPage } from '@/pages/terminals/terminal-new';
import { TerminalDetailPage } from '@/pages/terminals/terminal-detail';
import { TerminalPeripheralConfigPage } from './pages/terminals/terminal-peripheral-config';
import { TerminalProfilesPage } from '@/pages/terminals/terminal-profiles';
import { TerminalLogsPage } from '@/pages/terminals/terminal-logs';
import { CountryPacksListPage } from '@/pages/country-packs/country-packs-list';
import { CountryPackTaxRatesPage } from '@/pages/country-packs/country-pack-tax-rates';
import { CatalogHotkeysPage } from '@/pages/catalog-hotkeys/catalog-hotkeys-page';
import { CashiersListPage } from '@/pages/employees/cashiers-list';
import { CashierNewPage } from '@/pages/employees/cashier-new';
import { CashierDetailPage } from '@/pages/employees/cashier-detail';
import { ShiftsListPage } from '@/pages/shifts/shifts-list';
import { ShiftDetailPage } from '@/pages/shifts/shift-detail';
import { CashMovementsPage } from '@/pages/shifts/cash-movements';
import { TurnoverPage } from '@/pages/reports/turnover';
import { ReturnsReportPage } from '@/pages/reports/returns';
import { PromotionDiscountReportPage } from '@/pages/reports/promotion-discounts';
import { PaymentsReconciliationPage } from '@/pages/payments/reconciliation-queue';
import { WeightCoveragePage } from '@/pages/weight/weight-coverage';
import { ApprovalsPage } from '@/pages/approvals/approvals-page';
import { ReceiptsListPage } from '@/pages/receipts/receipts-list';
import { ReceiptDetailPage } from '@/pages/receipts/receipt-detail';
import { LegacyReceiptImportPage } from '@/pages/receipts/legacy-receipt-import';
import { PromotionsListPage } from '@/pages/promotions/promotions-list';
import { PromotionNewPage } from '@/pages/promotions/promotion-new';
import { LoyaltyCampaignImportPage } from '@/pages/promotions/campaign-import';
import { PromotionDetailPage } from '@/pages/promotions/promotion-detail';
import { FiscalSubmissionsPage } from '@/pages/fiscal-cabinet/fiscal-submissions';
import { FiscalSignatureLogPage } from '@/pages/fiscal-cabinet/fiscal-signature-log';
import { FiscalJournalPage } from '@/pages/fiscal-cabinet/fiscal-journal';
import { FiscalObjectTreePage } from '@/pages/fiscal-cabinet/fiscal-object-tree';
import { FiscalSigningKeysPage } from '@/pages/fiscal-cabinet/fiscal-signing-keys';
import { FiscalLegalEntitiesPage } from '@/pages/fiscal-cabinet/fiscal-legal-entities';
import { CertificatesListPage } from '@/pages/certificates/certificates-list';
import { CertificateNewPage } from '@/pages/certificates/certificate-new';
import { CertificateDetailPage } from '@/pages/certificates/certificate-detail';
import { LocationsListPage } from '@/pages/locations/locations-list';
import { LocationNewPage } from '@/pages/locations/location-new';
import { LocationDetailPage } from '@/pages/locations/location-detail';
import { ApiDocsPage } from '@/pages/api-docs';
import { I18nStringsPage } from '@/pages/i18n/i18n-strings';
import { RolesPage } from '@/pages/roles/roles-page';
import { UsersPage } from '@/pages/users/users-page';
import { ReceiptConfigEditorPage } from '@/pages/receipt-config/receipt-config-editor';
import { TerminalFlowsListPage } from '@/pages/terminal-flows/terminal-flows-list';
import { FlowEditorPage } from '@/pages/terminal-flows/flow-editor';
import { ScreenFlowsListPage } from '@/pages/screen-flows/screen-flows-list';
import { JourneyEditorPage } from '@/pages/screen-flows/journey-editor';
import { ProvisionPage } from '@/pages/provision/provision-page';
import { DashboardWidgetsPage } from '@/pages/settings/dashboard-widgets';
import { IntegrationsPage } from '@/pages/settings/integrations-page';
import { UiThemesPage } from '@/pages/ui-themes/ui-themes-page';
import { ParametersPage } from '@/pages/parameters/parameters-page';
import { SecureScaleRoute } from '@/securescale/securescale-route';
import { RequireAuth } from '@/auth/require-auth';
import { bootTheme } from '@/theme/use-theme';
import '@/styles/globals.css';

// Apply persisted theme before first paint so the initial render
// doesn't flash the wrong palette.
bootTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (root === null) throw new Error('#root not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell>
                    <Outlet />
                  </AppShell>
                </RequireAuth>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/catalog/items" element={<ItemsListPage />} />
              <Route path="/catalog/items/new" element={<ItemNewPage />} />
              <Route path="/catalog/items/:id" element={<ItemDetailPage />} />
              <Route path="/catalog/categories" element={<CategoriesPage />} />
              <Route path="/pricing/price-lists" element={<PriceListsPage />} />
              <Route path="/import-export/csv" element={<CsvImportPage />} />
              <Route path="/sanitex/item-diagnosis" element={<SanitexItemDiagnosisPage />} />
              <Route
                path="/sanitex/pending-card-authorisations"
                element={<SanitexPendingCardAuthorisationsPage />}
              />
              <Route path="/terminals" element={<TerminalsListPage />} />
              <Route path="/terminals/kitchen-orders" element={<KitchenOrdersPage />} />
              <Route path="/terminals/fiscal-holds" element={<FiscalHoldsListPage />} />
              <Route path="/terminals/link" element={<LinkDevicesPage />} />
              <Route path="/terminals/new" element={<TerminalNewPage />} />
              <Route path="/terminals/:id" element={<TerminalDetailPage />} />
              <Route path="/terminals/:id/hardware" element={<TerminalPeripheralConfigPage />} />
              <Route path="/terminal-profiles" element={<TerminalProfilesPage />} />
              <Route path="/terminal-logs" element={<TerminalLogsPage />} />
              <Route path="/country-packs" element={<CountryPacksListPage />} />
              <Route path="/country-packs/:id/tax-rates" element={<CountryPackTaxRatesPage />} />
              <Route path="/catalog-hotkeys" element={<CatalogHotkeysPage />} />
              <Route path="/ui-themes" element={<UiThemesPage />} />
              <Route path="/parameters" element={<ParametersPage />} />
              <Route path="/employees" element={<CashiersListPage />} />
              <Route path="/employees/new" element={<CashierNewPage />} />
              <Route path="/employees/:id" element={<CashierDetailPage />} />
              <Route path="/shifts" element={<ShiftsListPage />} />
              <Route path="/shifts/:id" element={<ShiftDetailPage />} />
              <Route path="/cash-movements" element={<CashMovementsPage />} />
              <Route path="/reports/turnover" element={<TurnoverPage />} />
              <Route path="/reports/returns" element={<ReturnsReportPage />} />
              <Route
                path="/reports/promotion-discounts"
                element={<PromotionDiscountReportPage />}
              />
              <Route path="/payments/reconciliation" element={<PaymentsReconciliationPage />} />
              <Route path="/weight/coverage" element={<WeightCoveragePage />} />
              {/* SSC-95: SecureScale manager pages; nested routes live in the package. */}
              <Route path="/securescale/*" element={<SecureScaleRoute />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/receipts" element={<ReceiptsListPage />} />
              <Route path="/receipts/legacy-import" element={<LegacyReceiptImportPage />} />
              <Route path="/receipts/:id" element={<ReceiptDetailPage />} />
              <Route path="/promotions" element={<PromotionsListPage />} />
              <Route path="/promotions/new" element={<PromotionNewPage />} />
              <Route path="/promotions/campaign-import" element={<LoyaltyCampaignImportPage />} />
              <Route path="/promotions/:id" element={<PromotionDetailPage />} />
              <Route
                path="/fiscal-cabinet"
                element={<Navigate to="/fiscal-cabinet/submissions" replace />}
              />
              <Route path="/fiscal-cabinet/submissions" element={<FiscalSubmissionsPage />} />
              <Route path="/fiscal-cabinet/signature-log" element={<FiscalSignatureLogPage />} />
              <Route path="/fiscal-cabinet/journal" element={<FiscalJournalPage />} />
              <Route path="/fiscal-cabinet/object-tree" element={<FiscalObjectTreePage />} />
              <Route path="/fiscal-cabinet/legal-entities" element={<FiscalLegalEntitiesPage />} />
              <Route path="/fiscal-cabinet/signing-keys" element={<FiscalSigningKeysPage />} />
              <Route path="/certificates" element={<CertificatesListPage />} />
              <Route path="/certificates/new" element={<CertificateNewPage />} />
              <Route path="/certificates/:id" element={<CertificateDetailPage />} />
              <Route path="/locations" element={<LocationsListPage />} />
              <Route path="/locations/new" element={<LocationNewPage />} />
              <Route path="/locations/:id" element={<LocationDetailPage />} />
              <Route path="/provision" element={<ProvisionPage />} />
              <Route path="/settings/dashboard-widgets" element={<DashboardWidgetsPage />} />
              <Route path="/settings/integrations" element={<IntegrationsPage />} />
              <Route path="/api-docs" element={<ApiDocsPage />} />
              <Route path="/i18n/strings" element={<I18nStringsPage />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/receipt-config/editor" element={<ReceiptConfigEditorPage />} />
              <Route path="/terminal-flows" element={<TerminalFlowsListPage />} />
              <Route path="/terminal-flows/:id" element={<FlowEditorPage />} />
              <Route path="/screen-flows" element={<ScreenFlowsListPage />} />
              <Route path="/screen-flows/:id" element={<JourneyEditorPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </App>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
